import * as T from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { renderSceneStill } from './still';
import { softwareRenderer } from './gpu';
import { snapshotCanvas } from './snapshot';

// A room seen from its open corner (the home, a venue): the light, a camera that turns with one finger or
// the mouse but never swings round behind the walls, a renderer sized to its box, and a still picture
// instead when the browser has no WebGL, so the screen never breaks. `tick` runs every frame.

export type Stage = ReturnType<typeof createStage>;

export function createStage(
  element: HTMLElement,
  options: {
    sky: string;
    dataSaver: boolean;
    tick: (dt: number) => void;
    ready: () => boolean;
    fps?: number;
    // after dark: a dim sky light and a cool moon instead of the sun (the room's own lamps carry it)
    night?: boolean;
  },
) {
  const scene = new T.Scene();
  scene.background = new T.Color(options.sky);
  // a soft sky fill, and a warm sun high over the open corner's left that throws real shadows into the room
  const night = options.night === true;
  scene.add(
    night ? new T.HemisphereLight('#8f9fcf', '#2a3040', 0.8) : new T.HemisphereLight('#fff6e6', '#6f7f6a', 1.1),
  );
  const sun = night ? new T.DirectionalLight('#aebcff', 0.9) : new T.DirectionalLight('#ffe9c7', 2.8);
  sun.position.set(-3, 9, 3.5);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.03;
  sun.shadow.radius = 3;
  scene.add(sun, sun.target);
  const camera = new T.PerspectiveCamera(36, 1, 0.1, 120);
  let renderer: T.WebGLRenderer | undefined;
  let canvas: HTMLCanvasElement;
  try {
    renderer = new T.WebGLRenderer({ antialias: true, powerPreference: 'low-power' });
    renderer.setPixelRatio(options.dataSaver ? 1 : Math.min(devicePixelRatio, 1.5));
    // richer colour, like a lit room rather than a flat drawing; shadows unless saving data
    renderer.toneMapping = T.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.shadowMap.type = T.PCFSoftShadowMap;
    // the room stands still; only people move: their shadows are redrawn a few times a second, not every frame
    renderer.shadowMap.autoUpdate = false;
    canvas = renderer.domElement;
  } catch {
    canvas = document.createElement('canvas');
  }
  const mode: '3d' | 'still' = renderer ? '3d' : 'still';
  const slow = renderer ? softwareRenderer(renderer) : true;
  let shadows = !options.dataSaver && !slow;
  // fewer pixels where every one is drawn without a graphics chip
  const ratio = (saver: boolean) => (slow ? 0.75 : saver ? 1 : Math.min(devicePixelRatio, 1.5));
  if (renderer) {
    renderer.shadowMap.enabled = shadows;
    renderer.setPixelRatio(ratio(options.dataSaver));
  }
  canvas.setAttribute('aria-hidden', 'true');
  element.appendChild(canvas);
  const controls = new OrbitControls(camera, canvas);
  controls.enablePan = false;
  controls.enableDamping = true;
  controls.minPolarAngle = 0.5;
  controls.maxPolarAngle = 1.35;
  // keep the open corner towards the camera, so the walls never hide the room
  controls.minAzimuthAngle = 0.15;
  controls.maxAzimuthAngle = Math.PI / 2 - 0.15;
  controls.touches = { ONE: T.TOUCH.ROTATE, TWO: T.TOUCH.DOLLY_ROTATE };

  // look at a floor of this size from the open corner
  const frame = (floor: { w: number; d: number }, distance = 1) => {
    const span = Math.max(floor.w, floor.d);
    light(floor);
    controls.target.set(0, 0.9, 0);
    controls.minDistance = span * 0.8;
    controls.maxDistance = span * 2.2;
    camera.position.set(span * 1.1 * distance, span * 0.95 * distance, span * 1.2 * distance);
    controls.update();
  };

  // fit the whole floor in view whatever the shape of the screen; `lift` (0–1) moves the room up the
  // screen, for a bar that covers the bottom of it
  let fitted: { floor: { w: number; d: number }; lift: number } | null = null;
  // looking down on the room from high over the open corner, like Lagos Life, so the floor fills the phone
  const direction = new T.Vector3(1, 1.25, 1.05).normalize();
  // the sun's shadow box hugs the room, so its shadows stay sharp
  const light = (floor: { w: number; d: number }) => {
    const span = Math.max(floor.w, floor.d) / 2 + 1.5;
    const shadow = sun.shadow.camera;
    shadow.left = -span;
    shadow.right = span;
    shadow.top = span;
    shadow.bottom = -span;
    shadow.near = 0.5;
    shadow.far = 30;
    shadow.updateProjectionMatrix();
    sun.position.set(-span * 0.55, 9, span * 0.6);
    sun.target.position.set(0, 0, 0);
  };
  const fit = (floor: { w: number; d: number }, lift = 0) => {
    fitted = { floor, lift };
    light(floor);
    // the floor seen from above the corner is narrower than its full diagonal: fill the screen with it
    const radius = ((floor.w + floor.d) / 2) * 0.72 + 0.2;
    const vertical = T.MathUtils.degToRad(camera.fov);
    const horizontal = 2 * Math.atan(Math.tan(vertical / 2) * camera.aspect);
    const distance = radius / Math.tan(Math.min(vertical, horizontal) / 2);
    controls.target.set(0, 0.6 - radius * lift, 0);
    camera.position.copy(direction).multiplyScalar(distance).add(controls.target);
    controls.minDistance = distance * 0.45;
    controls.maxDistance = distance * 1.4;
    controls.update();
  };

  let stillFrame = 0;
  const requestStill = () => {
    if (renderer || stillFrame) return;
    stillFrame = requestAnimationFrame(() => {
      stillFrame = 0;
      if (options.ready()) renderSceneStill(scene, camera, canvas, element.clientWidth, element.clientHeight);
    });
  };
  const size = { width: 0, height: 0 };
  const resize = () => {
    const width = element.clientWidth,
      height = element.clientHeight;
    if (!width || !height) return;
    size.width = width;
    size.height = height;
    camera.aspect = width / height;
    camera.fov = width < 640 ? 46 : 36;
    camera.updateProjectionMatrix();
    renderer?.setSize(width, height);
    if (fitted) fit(fitted.floor, fitted.lift);
    requestStill();
  };
  const observer = new ResizeObserver(resize);
  observer.observe(element);
  resize();

  // frames are capped (25 a second by default) to spare phone batteries
  const gap = 1000 / (options.fps ?? 25) - 2;
  let tick = 0,
    last = 0,
    shadowAge = 1;
  const loop = (t: number) => {
    tick = requestAnimationFrame(loop);
    if (t - last < gap || document.hidden) return;
    const dt = last ? Math.min((t - last) / 1000, 0.08) : 0;
    last = t;
    options.tick(dt);
    controls.update();
    if (renderer && shadows && (shadowAge += dt) > 0.2) {
      shadowAge = 0;
      renderer.shadowMap.needsUpdate = true;
    }
    renderer?.render(scene, camera);
  };
  if (renderer) tick = requestAnimationFrame(loop);

  return {
    scene,
    camera,
    canvas,
    controls,
    mode,
    // drawing without a graphics chip: no shadows, no lamp light
    slow,
    size,
    frame,
    fit,
    requestStill,
    // a photo of the room as it is now (the in-game camera)
    snapshot: () =>
      snapshotCanvas(canvas, () => {
        if (renderer) renderer.render(scene, camera);
        else renderSceneStill(scene, camera, canvas, element.clientWidth, element.clientHeight);
      }),
    saver(on: boolean) {
      renderer?.setPixelRatio(ratio(on));
      if (renderer) {
        shadows = !on && !slow;
        renderer.shadowMap.enabled = shadows;
        renderer.shadowMap.needsUpdate = true;
        scene.traverse((object) => {
          if (object instanceof T.Mesh) (object.material as T.Material).needsUpdate = true;
        });
      }
      resize();
    },
    dispose() {
      cancelAnimationFrame(tick);
      cancelAnimationFrame(stillFrame);
      observer.disconnect();
      controls.dispose();
      renderer?.dispose();
      renderer?.forceContextLoss();
      canvas.remove();
    },
  };
}
